import AspectRatio from "@blocks/AspectRatio";
import Flex from '@blocks/Flex';
import Grid from "@blocks/Grid";
import Select from '@blocks/Select';
import { pluralize } from "@helpers";
import useParticipationAwardController from '@jury/api/useParticipationAwardController';
import { colors, typography } from '@styles/vars';
import React, { useRef } from 'react';
import LazyLoad from "react-lazyload";
import styled from 'styled-components';
import { useSalonAcceptancesContext } from '../context';

export default function Acceptance({ data: acceptance, idx }: ArchiveComponentPropsWithIdx<Jury.Resources.Acceptance>) {
    const awardsRef = useRef<any>()
    const { countAcceptancesByCustomer, gallery, salon, handleAcceptanceChange } = useSalonAcceptancesContext()

    const award = acceptance.award ? ({ value: acceptance.award.id.toString(), label: acceptance.award.name }) : null
    const canGrantAwards = salon.methods.hasAwardAbilityIn(acceptance.section.id)

    const controller = useParticipationAwardController(acceptance.id)
    const handleChange = async (awardId?: string | number) => {
        try {
            const acceptance = await controller.store({ award_id: awardId })
            handleAcceptanceChange(acceptance)    
        } catch {
            awardsRef.current?.select.clearValue()
        }
    }

    const awardsCount = countAcceptancesByCustomer(acceptance.photo.user.id)

    return (
        <LazyLoad height="200px" offset={100}>
            <Grid gap="10px">
                <AspectRatio height={acceptance.photo.height} width={acceptance.photo.width}>
                    <ImageWrapper>
                        <ImageContainer src={acceptance.photo.url} onClick={() => gallery.open(idx)} />
                    </ImageWrapper>
                </AspectRatio>
                <Grid gap="10px">
                    <Flex justifyContent="space-between">
                        <Grid gap="5px">
                            <h4><Identifier>#{idx + 1}</Identifier><span>{acceptance.photo.title}</span></h4>
                            {awardsCount.awards > 0 && (
                                <small>
                                    <span>The author has </span>
                                    <b>{pluralize(awardsCount.awards, 'award')}</b>
                                    <span> in </span>
                                    <b>{pluralize(awardsCount.sections, 'section')}</b>
                                </small>
                            )}
                            <small>Section: {acceptance.section.name}</small>
                            {(acceptance.award && !canGrantAwards) ? <small><b>Award: {acceptance.award.name}</b></small> : null}
                        </Grid>
                        <VoteAvg>
                            <small>Sum:</small>
                            <p>{acceptance.votes_sum_score}</p>
                        </VoteAvg>
                    </Flex>
                    <div>
                        {canGrantAwards && (
                            <Select 
                                ref={awardsRef}
                                id={`awards-${acceptance.id}`}
                                name={`awards-${acceptance.id}`}
                                height="s"
                                options={salon.attrs.awards.options()}
                                defaultValue={award}
                                isClearable
                                onChange={option => handleChange(option?.value)} />
                        )}
                    </div>
                </Grid>
            </Grid>
        </LazyLoad>
    )
}

// STYLES
const VoteAvg = styled.div`
    display: flex;
    align-items: flex-start;

    small {
        margin-right: 5px;
    }

    p {
        font-size: 36px;
        font-weight: 600;
        color: ${colors.black};
        line-height: 1;
    }
`

const Identifier = styled.span`
    font-size: ${typography.sizes.s};
    color: ${colors.darkGrey};
    margin-right: 6px;
`

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`

const ImageContainer = styled.div<{ src: string, onClick?: any }>`
    position: relative;
    width: 100%;
    height: 100%;
    background: ${({ src }) => `url(${src}) center center no-repeat`};
    background-size: cover;
    border-radius: 5px;
    cursor: ${props => props.onClick ? "zoom-in" : "auto"};
    transition: .2s ease-out;

    &:hover {
        transform: scale(1.02);
    }
`